import { api } from '@/services/config';
import { deleteCookie, setCookie } from '@/utils/cookies';
import { getLocalStorageWithoutParse, removeLocalStorage, setLocalStorageWithoutParse } from '@/utils/local-storage';

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
  idempotencyKey?: string;
  /** Só aplica quando idempotencyKey está presente (retry sem key pode duplicar). */
  maxRetries?: number;
  retryBaseDelayMs?: number;
}

interface ApiErrorResponse {
  code?: string;
  message?: string;
  [key: string]: unknown;
}

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Powered-By': 'BilheteriaWeb',
};

const defaultHeadersFile: Record<string, string> = {
  'Content-Type': 'application/octet-stream',
  Accept: 'application/octet-stream',
  'X-Powered-By': 'BilheteriaWeb',
};

export const AUTH = {
  ACCESS_TOKEN: 'u_ac_t',
  REFRESH_TOKEN: 'u_r_t',
  // SECURITY: Auth state flag (not the actual token) for UI state management
  // The actual token is stored in an HttpOnly cookie by the API
  AUTH_STATE: 'u_auth_state',
};

// Detecta se está rodando em mobile ou PWA (standalone)
// Mobile browsers têm problemas com HttpOnly cookies, então usamos localStorage como fallback
export const isMobileOrPWA = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Detecta modo standalone (PWA instalado)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

  // Detecta user agent mobile
  const isMobileUA = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return isStandalone || isMobileUA;
};

export const getSessionTokens = () => ({
  // SECURITY: Tokens are now stored in HttpOnly cookies - not readable via JavaScript
  // The browser automatically sends HttpOnly cookies with credentials: 'include'
  // This function now only checks if user is authenticated (not the actual token)
  accessToken: getLocalStorageWithoutParse(AUTH.AUTH_STATE) === 'authenticated' ? 'cookie-auth' : '',
  refreshToken: '',
});

export const setSessionTokens = (tokens: { accessToken: string; refreshToken: string }) => {
  if (tokens.accessToken) {
    setLocalStorageWithoutParse(AUTH.AUTH_STATE, 'authenticated');

    // SEMPRE salvar o token real no localStorage
    // Isso garante fallback universal para quando cookies não funcionam (mobile, PWA, etc)
    if (tokens.accessToken !== 'cookie-auth' && tokens.accessToken !== 'authenticated') {
      setLocalStorageWithoutParse(AUTH.ACCESS_TOKEN, tokens.accessToken);
    }

    // Also set a cookie so middleware can detect auth state server-side
    setCookie({
      name: AUTH.AUTH_STATE,
      value: 'authenticated',
      path: '/',
      maxAge: 60 * 60 * 24 * 30, // 30 dias em segundos
      sameSite: 'lax',
      secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
    });
  }
};

export const removeSessionTokens = () => {
  // Clear auth state flag from both localStorage and cookie
  removeLocalStorage(AUTH.AUTH_STATE);
  deleteCookie(AUTH.AUTH_STATE);
  // Legacy cleanup - remove any old tokens that may exist
  removeLocalStorage(AUTH.ACCESS_TOKEN);
  removeLocalStorage(AUTH.REFRESH_TOKEN);
  deleteCookie(AUTH.ACCESS_TOKEN);
  deleteCookie(AUTH.REFRESH_TOKEN);
};

function isTransientError(error: unknown): boolean {
  if (!error) return true;
  if (typeof error === 'object' && error !== null) {
    const e = error as { status?: number; name?: string; code?: string };
    if (e.name === 'AbortError' || e.name === 'TypeError') return true;
    if (e.code === 'NETWORK_ERROR' || e.code === 'UNKNOWN_ERROR') return true;
    if (typeof e.status === 'number' && e.status >= 500 && e.status < 600) return true;
  }
  return false;
}

export default async function customFetch(url: string, options: RequestOptions = {}): Promise<any> {
  // Requests now go through Next.js proxy (same-origin)
  // This eliminates cross-domain cookie issues on mobile devices

  const headers: Record<string, string> = {
    ...defaultHeaders,
    ...options.headers,
  };

  // SEMPRE enviar Authorization header se tiver token no localStorage
  // Funciona como fallback universal para quando cookies não funcionam
  const token = getLocalStorageWithoutParse(AUTH.ACCESS_TOKEN);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (options.idempotencyKey) {
    headers['X-Idempotency-Key'] = options.idempotencyKey;
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // Sends cookies automatically (fallback)
  };

  const maxRetries = options.idempotencyKey ? (options.maxRetries ?? 0) : 0;
  const baseDelay = options.retryBaseDelayMs ?? 1000;

  const doFetch = async (): Promise<any> =>
    fetch(api.baseUrl + url, requestOptions).then(async (res) => {
      if (res.status === 204) return res.status === 204 ? {} : res.json();
      if (!res.ok) {
        try {
          const err = (await res.json()) as ApiErrorResponse;

          throw {
            status: res.status,
            statusText: res.statusText,
            code: err.code,
            message: err.message,
            ...err,
          };
        } catch (jsonError) {
          if (jsonError && typeof jsonError === 'object' && 'code' in jsonError) {
            throw jsonError;
          }

          console.error(
            `Erro ao fazer parse do JSON de erro: ${options.method || 'GET'} ${url} respondeu ${res.status} ` +
              `com content-type ${res.headers.get('content-type') || 'ausente'}`,
            jsonError
          );
          throw {
            status: res.status,
            statusText: res.statusText,
            code: res.status === 401 ? 'UNAUTHORIZED' : 'UNKNOWN_ERROR',
            message: res.statusText || 'Erro desconhecido',
          };
        }
      }
      return res.json();
    });

  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await doFetch();
    } catch (error) {
      lastError = error;
      if (attempt === maxRetries || !isTransientError(error)) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

export async function customFetchFile(url: string, options: RequestOptions = {}, asBlob = false): Promise<any> {
  // Requests now go through Next.js proxy (same-origin)
  const headers: Record<string, string> = {
    ...defaultHeadersFile,
    ...options.headers,
  };

  // SEMPRE enviar Authorization header se tiver token no localStorage
  const token = getLocalStorageWithoutParse(AUTH.ACCESS_TOKEN);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // Sends cookies automatically (fallback)
  };

  return fetch(api.baseUrl + url, requestOptions)
    .then(async (res) => {
      if (!res.ok) {
        const err = (await res.json()) as ApiErrorResponse;
        throw {
          status: res.status,
          statusText: res.statusText,
          code: err.code,
          message: err.message,
          ...err,
        };
      }
      if (asBlob) {
        return res.blob();
      }
      return res.arrayBuffer();
    })
    .then((data) => {
      if (asBlob) return data;
      return Buffer.from(data as ArrayBuffer).toString('base64');
    });
}
